import { useI18n } from "providers/I18nProvider";
import { useNavigate } from "react-router-dom";
import {
  gql,
  VerificationRequestActionType,
  VerificationSeachParams,
} from "types";
import { useSafeMutation } from "./useSafeMutation";
import { UseTypedSearchParams } from "./useTypedSearchParams";

export type VerificationCallback = (signature: string) => Promise<void>;

export type VerificationRequestOptions = {
  actionType: VerificationRequestActionType;
  verificationCallback: VerificationCallback;
};

const sendCodeMutation = gql(/* GraphQL */ `
  mutation SendVerificationCodeHook($verificationRequestId: String!) {
    sendVerificationCode(verificationRequestId: $verificationRequestId) {
      id
      actionType
      validTill
    }
  }
`);

const createVerificationRequestMutation = gql(/* GraphQL */ `
  mutation CreateVerificationRequest(
    $actionType: VerificationRequestActionType!
  ) {
    createVerificationRequest(actionType: $actionType) {
      id
    }
  }
`);

/**
 * To create a request, we need a signature. The signature is created with e.g.:
 * an OTP on the VerificationPage. Since the VerificationPage is generic and can
 * be used to create signatures for various actions, we need some way of passing
 * non-serialisable state to it (mutation call). So we can't use the typical nav
 * state for the purpose.
 * This is a workaround - the useVerifiedRequest() hook receives a function with
 * a signature parameter as an argument, assigns it to this global variable, and
 * the VerificationPage imports it and calls it with the signature.
 */
export let onRequestVerifiedCb: VerificationCallback | undefined;

/**
 * @example
 * const [mutate] = useSafeMutation(mutation);
 * const act = useVerifiedRequest({
 *   actionType: VerificationRequestActionType.Action,
 *   verificationCallback: async (signature) => {
 *     const res = await mutate({variables});
 *     if (res.errors?.length) return;
 *     navigate(ROUTES.success);
 *   },
 * });
 */
export function useVerifiedRequest(opts: VerificationRequestOptions) {
  const [createVerificationRequest] = useSafeMutation(
    createVerificationRequestMutation,
    { variables: { actionType: opts.actionType } },
  );
  const [sendCode] = useSafeMutation(sendCodeMutation);
  const navigate = useNavigate();

  const { language } = useI18n();

  return async () => {
    const { data } = await createVerificationRequest();
    const requestId = data?.createVerificationRequest.id;
    if (!requestId) return;

    onRequestVerifiedCb = opts.verificationCallback;
    const search =
      new URLSearchParams() as UseTypedSearchParams<VerificationSeachParams>;
    search.set("verificationRequestId", requestId);
    void sendCode({ variables: { verificationRequestId: requestId } });
    void navigate({
      pathname: `/${language}/verification`,
      search: search.toString(),
    });
  };
}
